import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, MousePointer2, Pencil, SlidersHorizontal, ChevronDown } from "lucide-react";

// ===========================================================================
// EditToolbar (Menu) — a row of chip buttons below the header on the chat page.
//
// The chips drive the right-docked ResultPreviewPanel (which owns the preview
// iframe) via the `vella:toolbar` window event: { action, active?, value? }.
//   • Mark up / Comments / Tweaks → toggle a preview mode (active bool)
//   • 100% (zoom) → { action:'zoom', value:<pct> } via a dropdown
//   • Present → { action:'present', value:'fullscreen'|'newtab'|'share' }
//   • Edit → keeps its existing `vella:editToolbar` event (opens the panel)
// ===========================================================================

const FONT = '-apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif';

const STYLE_ID = "edit-toolbar-css";
const CSS = `
.vella-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 9999px;
  font-family: ${FONT};
  font-size: 13px;
  font-weight: 400;
  color: #565c5e;
  background: transparent;
  border: 1px solid #dfe2e3;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(15,23,42,0.06);
  transition: background 160ms ease-out, color 160ms ease-out, border-color 160ms ease-out;
}
.vella-chip:hover { background: #f0f1f2; }
.vella-chip:focus-visible { outline: 2px solid #275c73; outline-offset: 2px; }
.vella-chip.is-active {
  color: #fff;
  background: #275c73;
  border-color: #275c73;
}
.vella-tb-menu {
  position: absolute; top: calc(100% + 6px); left: 50%; transform: translateX(-50%);
  min-width: 132px; padding: 4px; z-index: 30;
  background: #fff; border: 1px solid #dfe2e3; border-radius: 12px;
  box-shadow: 0 10px 30px -10px rgba(15,23,42,0.28);
  display: flex; flex-direction: column; gap: 2px;
}
.vella-tb-item {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 7px 12px; border-radius: 8px; border: none; background: transparent;
  font-family: ${FONT}; font-size: 13px; color: #33383a; cursor: pointer; text-align: left;
}
.vella-tb-item:hover { background: #f4f5f6; }
.vella-tb-item.on { color: #275c73; font-weight: 600; }
`;

const ZOOMS = [50, 75, 100, 125, 150];
const PRESENTS = [
  { value: "fullscreen", label: "Fullscreen" },
  { value: "newtab", label: "Open in new tab" },
  { value: "share", label: "Copy link" },
];

function dispatchToolbar(action, extra) {
  window.dispatchEvent(new CustomEvent("vella:toolbar", { detail: { action, ...(extra || {}) } }));
}

export default function EditToolbar() {
  const [activeId, setActiveId] = useState(null); // markup | comments | tweaks
  const [editOn, setEditOn] = useState(false);    // inline text editing in the preview
  const [zoom, setZoom] = useState(100);
  const [menu, setMenu] = useState(null); // 'zoom' | 'present' | null
  const rootRef = useRef(null);

  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);

  // Close any open dropdown on outside click / Escape.
  useEffect(() => {
    if (!menu) return;
    const onDoc = (e) => { if (!rootRef.current || !rootRef.current.contains(e.target)) setMenu(null); };
    const onKey = (e) => { if (e.key === "Escape") setMenu(null); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [menu]);

  const toggleMode = (id) => {
    setActiveId((cur) => {
      const next = cur === id ? null : id;
      // Turn the previously-active mode off, then set the new one.
      if (cur && cur !== id) dispatchToolbar(cur, { active: false });
      dispatchToolbar(id, { active: next === id });
      return next;
    });
  };

  const chooseZoom = (pct) => { setZoom(pct); setMenu(null); dispatchToolbar("zoom", { value: pct }); };
  const choosePresent = (value) => { setMenu(null); dispatchToolbar("present", { value }); };

  const chip = (opts) => (
    <button
      type="button"
      data-no-vella-ring="true"
      className={`vella-chip${opts.active ? " is-active" : ""}`}
      aria-pressed={opts.pressable ? !!opts.active : undefined}
      onClick={opts.onClick}
    >
      {opts.plus && <span style={{ fontSize: 14, lineHeight: 1, marginRight: -1 }}>+</span>}
      {opts.Icon && <opts.Icon size={14} strokeWidth={1.7} />}
      <span>{opts.label}</span>
      {opts.chevron && <ChevronDown size={13} strokeWidth={1.7} />}
    </button>
  );

  return (
    <div
      ref={rootRef}
      role="toolbar"
      aria-label="Edit toolbar"
      style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        flexWrap: "wrap", padding: "8px 24px", background: "transparent",
        position: "relative", zIndex: 10,
      }}
    >
      {chip({ label: "Mark up", Icon: MousePointer2, pressable: true, active: activeId === "markup", onClick: () => toggleMode("markup") })}
      {chip({ label: "Comments", Icon: MessageSquare, pressable: true, active: activeId === "comments", onClick: () => toggleMode("comments") })}
      {chip({ label: "Edit", Icon: Pencil, pressable: true, active: editOn, onClick: () => {
        setEditOn((cur) => {
          const next = !cur;
          // Legacy event opens the split preview (or toasts when no website
          // exists yet); the vella:toolbar event drives inline text editing.
          if (next) window.dispatchEvent(new CustomEvent("vella:editToolbar", { detail: { action: "edit" } }));
          dispatchToolbar("edit", { active: next });
          return next;
        });
      } })}
      {chip({ label: "Tweaks", Icon: SlidersHorizontal, plus: true, pressable: true, active: activeId === "tweaks", onClick: () => toggleMode("tweaks") })}

      {/* Zoom dropdown */}
      <div style={{ position: "relative" }}>
        {chip({ label: `${zoom}%`, chevron: true, onClick: () => setMenu((m) => (m === "zoom" ? null : "zoom")) })}
        {menu === "zoom" && (
          <div className="vella-tb-menu" role="menu">
            {ZOOMS.map((z) => (
              <button key={z} type="button" role="menuitem" className={`vella-tb-item${z === zoom ? " on" : ""}`} onClick={() => chooseZoom(z)}>
                <span>{z}%</span>{z === zoom && <span>✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Present dropdown */}
      <div style={{ position: "relative" }}>
        {chip({ label: "Present", chevron: true, onClick: () => setMenu((m) => (m === "present" ? null : "present")) })}
        {menu === "present" && (
          <div className="vella-tb-menu" role="menu">
            {PRESENTS.map((p) => (
              <button key={p.value} type="button" role="menuitem" className="vella-tb-item" onClick={() => choosePresent(p.value)}>
                <span>{p.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
